import { createElement } from 'react';
import { alpha, type Theme } from '@mui/material/styles';
import type { PickerComponents } from '@mui/x-date-pickers/themeAugmentation';
import { pickersYearClasses, pickersMonthClasses, pickersDayClasses } from '@mui/x-date-pickers';
import { menuItemClasses } from '@mui/material/MenuItem';
import { svgIconClasses } from '@mui/material/SvgIcon';

import { IconCalendar } from '@/components/icon/tabler/IconCalendar';
import { IconClock } from '@/components/icon/tabler/IconClock';
import { IconX } from '@/components/icon/tabler/IconX';
import { IconChevronDown } from '@/components/icon/tabler/IconChevronDown';
import { IconChevronLeft } from '@/components/icon/tabler/IconChevronLeft';
import { IconChevronRight } from '@/components/icon/tabler/IconChevronRight';
import { gray, controlTokens, surfaceTokens } from '@/theme/themePrimitives';

export const datePickersCustomizations: PickerComponents<Theme> = {
  MuiDateTimePickerTabs: {
    defaultProps: { dateIcon: createElement(IconCalendar), timeIcon: createElement(IconClock) },
  },
  MuiDateTimeField: { defaultProps: { slots: { clearIcon: IconX } } },
  MuiTimeField: { defaultProps: { slots: { clearIcon: IconX } } },
  MuiDateField: { defaultProps: { slots: { clearIcon: IconX } } },
  MuiMobileTimePicker: {
    defaultProps: { slots: { leftArrowIcon: IconChevronLeft, rightArrowIcon: IconChevronRight } },
  },
  MuiDesktopTimePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: IconClock,
        clearIcon: IconX,
        leftArrowIcon: IconChevronLeft,
        rightArrowIcon: IconChevronRight,
      },
    },
  },
  MuiTimePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: IconClock,
        clearIcon: IconX,
        leftArrowIcon: IconChevronLeft,
        rightArrowIcon: IconChevronRight,
      },
    },
  },
  MuiMobileDateTimePicker: {
    defaultProps: { slots: { leftArrowIcon: IconChevronLeft, rightArrowIcon: IconChevronRight } },
  },
  MuiDesktopDateTimePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: IconCalendar,
        clearIcon: IconX,
        leftArrowIcon: IconChevronLeft,
        rightArrowIcon: IconChevronRight,
        switchViewIcon: IconChevronDown,
      },
    },
  },
  MuiDateTimePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: IconCalendar,
        clearIcon: IconX,
        leftArrowIcon: IconChevronLeft,
        rightArrowIcon: IconChevronRight,
        switchViewIcon: IconChevronDown,
      },
    },
  },
  MuiMobileDatePicker: {
    defaultProps: { slots: { leftArrowIcon: IconChevronLeft, rightArrowIcon: IconChevronRight } },
  },
  MuiDesktopDatePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: IconCalendar,
        clearIcon: IconX,
        leftArrowIcon: IconChevronLeft,
        rightArrowIcon: IconChevronRight,
        switchViewIcon: IconChevronDown,
      },
    },
  },
  MuiDatePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: IconCalendar,
        clearIcon: IconX,
        leftArrowIcon: IconChevronLeft,
        rightArrowIcon: IconChevronRight,
        switchViewIcon: IconChevronDown,
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: ({ theme }) => ({
        marginTop: 4,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${(theme.vars || theme).palette.divider}`,
        backgroundImage: 'none',
        background: (theme.vars || theme).palette.background.paper,
        boxShadow: surfaceTokens.shadowOverlay,
        [`& .${menuItemClasses.root}`]: {
          borderRadius: 6,
          margin: '0 6px',
        },
        ...theme.applyStyles('dark', {
          background: gray[900],
          boxShadow:
            'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
        }),
      }),
    },
  },
  MuiPickersArrowSwitcher: {
    defaultProps: { slots: { leftArrowIcon: IconChevronLeft, rightArrowIcon: IconChevronRight } },
    styleOverrides: {
      // Bordered 28px buttons without the `edge` negative margins keep the
      // chevrons 32px apart, as before, without the circles overlapping.
      spacer: { width: 4 },
      button: ({ theme }) => ({
        width: 28,
        height: 28,
        padding: 4,
        margin: 0,
        [`& .${svgIconClasses.root}`]: { fontSize: 18 },
        backgroundColor: 'transparent',
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiPickersCalendarHeader: {
    defaultProps: { slots: { switchViewIcon: IconChevronDown } },
    styleOverrides: {
      switchViewButton: {
        padding: 0,
        border: 'none',
      },
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: ({ theme }) => ({
        fontSize: theme.typography.body1.fontSize,
        color: (theme.vars || theme).palette.grey[600],
        padding: theme.spacing(0.5),
        borderRadius: theme.shape.borderRadius,
        '&:hover': {
          backgroundColor: (theme.vars || theme).palette.action.hover,
        },
        [`&.${pickersMonthClasses.selected}`]: {
          backgroundColor: controlTokens.primary,
          color: surfaceTokens.paper,
          fontWeight: theme.typography.fontWeightMedium,
        },
        '&:focus': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
          outlineOffset: '2px',
          backgroundColor: 'transparent',
          [`&.${pickersMonthClasses.selected}`]: { backgroundColor: controlTokens.primary },
        },
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[300],
          '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.hover,
          },
          [`&.${pickersMonthClasses.selected}`]: {
            color: (theme.vars || theme).palette.common.black,
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: gray[300],
          },
          '&:focus': {
            outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            [`&.${pickersMonthClasses.selected}`]: { backgroundColor: gray[300] },
          },
        }),
      }),
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      yearButton: ({ theme }) => ({
        fontSize: theme.typography.body1.fontSize,
        color: (theme.vars || theme).palette.grey[600],
        padding: theme.spacing(0.5),
        borderRadius: theme.shape.borderRadius,
        height: 'fit-content',
        '&:hover': {
          backgroundColor: (theme.vars || theme).palette.action.hover,
        },
        [`&.${pickersYearClasses.selected}`]: {
          backgroundColor: controlTokens.primary,
          color: surfaceTokens.paper,
          fontWeight: theme.typography.fontWeightMedium,
        },
        '&:focus': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
          outlineOffset: '2px',
          backgroundColor: 'transparent',
          [`&.${pickersYearClasses.selected}`]: { backgroundColor: controlTokens.primary },
        },
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[300],
          '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.hover,
          },
          [`&.${pickersYearClasses.selected}`]: {
            color: (theme.vars || theme).palette.common.black,
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: gray[300],
          },
          '&:focus': {
            outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            [`&.${pickersYearClasses.selected}`]: { backgroundColor: gray[300] },
          },
        }),
      }),
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.body1.fontSize,
        color: (theme.vars || theme).palette.grey[600],
        padding: theme.spacing(0.5),
        borderRadius: theme.shape.borderRadius,
        [`&.${pickersDayClasses.today}:not(.${pickersDayClasses.selected})`]: {
          borderColor: controlTokens.coral,
          color: controlTokens.primary,
        },
        '&:hover': {
          backgroundColor: (theme.vars || theme).palette.action.hover,
        },
        [`&.${pickersDayClasses.selected}`]: {
          backgroundColor: controlTokens.primary,
          color: surfaceTokens.paper,
          fontWeight: theme.typography.fontWeightMedium,
        },
        '&:focus': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
          outlineOffset: '2px',
          backgroundColor: 'transparent',
          [`&.${pickersDayClasses.selected}`]: { backgroundColor: controlTokens.primary },
        },
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[300],
          '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.hover,
          },
          [`&.${pickersDayClasses.selected}`]: {
            color: (theme.vars || theme).palette.common.black,
            fontWeight: theme.typography.fontWeightMedium,
            backgroundColor: gray[300],
          },
          '&:focus': {
            outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            [`&.${pickersDayClasses.selected}`]: { backgroundColor: gray[300] },
          },
        }),
      }),
    },
  },
};
